import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Users, 
  Plus, 
  Search, 
  Phone, 
  Mail,
  MapPin,
  ChevronRight,
  Ear,
  Calendar
} from "lucide-react";
import { toast } from "sonner";

export default function PatientsPage() {
  const { user, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newPatient, setNewPatient] = useState({
    name: "",
    age: "",
    gender: "Male",
    phone: "",
    email: "",
    address: "",
    clinic_id: "",
    medical_history: ""
  });

  useEffect(() => {
    fetchPatients();
    fetchClinics();
  }, []);

  const fetchPatients = async () => {
    try {
      const response = await apiClient.get("/patients");
      setPatients(response.data);
    } catch (error) {
      console.error("Failed to fetch patients:", error);
      toast.error("Failed to load patients");
    } finally {
      setLoading(false);
    }
  };

  const fetchClinics = async () => {
    try {
      const response = await apiClient.get("/clinics");
      setClinics(response.data);
      if (response.data.length > 0 && !newPatient.clinic_id) {
        setNewPatient(prev => ({ ...prev, clinic_id: response.data[0].id }));
      }
    } catch (error) {
      console.error("Failed to fetch clinics:", error);
    }
  };

  const handleCreatePatient = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...newPatient,
        age: parseInt(newPatient.age)
      };
      const response = await apiClient.post("/patients", payload);
      setPatients([response.data, ...patients]);
      toast.success("Patient created successfully!");
      setDialogOpen(false);
      setNewPatient({
        name: "",
        age: "",
        gender: "Male",
        phone: "",
        email: "",
        address: "",
        clinic_id: clinics[0]?.id || "",
        medical_history: ""
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create patient");
    }
  };

  const filteredPatients = patients.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.phone.includes(searchTerm)
  );

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="patients-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Patients</h1>
            <p className="text-slate-500">Manage patient records and hearing assessments</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-sky-600 hover:bg-sky-700" data-testid="add-patient-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Patient
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Add New Patient</DialogTitle>
                <DialogDescription>Enter patient details to create a new record</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreatePatient} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Full Name *</Label>
                    <Input
                      value={newPatient.name}
                      onChange={(e) => setNewPatient({...newPatient, name: e.target.value})}
                      placeholder="Patient name"
                      required
                      data-testid="patient-name-input"
                    />
                  </div>
                  <div>
                    <Label>Age *</Label>
                    <Input
                      type="number"
                      value={newPatient.age}
                      onChange={(e) => setNewPatient({...newPatient, age: e.target.value})}
                      placeholder="Age"
                      required
                      min="0"
                      max="120"
                    />
                  </div>
                  <div>
                    <Label>Gender *</Label>
                    <Select value={newPatient.gender} onValueChange={(v) => setNewPatient({...newPatient, gender: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Phone *</Label>
                    <Input
                      value={newPatient.phone}
                      onChange={(e) => setNewPatient({...newPatient, phone: e.target.value})}
                      placeholder="+91-9876543210"
                      required
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={newPatient.email}
                      onChange={(e) => setNewPatient({...newPatient, email: e.target.value})}
                      placeholder="email@example.com"
                    />
                  </div>
                  {isSuperAdmin && (
                    <div className="col-span-2">
                      <Label>Clinic *</Label>
                      <Select value={newPatient.clinic_id} onValueChange={(v) => setNewPatient({...newPatient, clinic_id: v})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select clinic" />
                        </SelectTrigger>
                        <SelectContent>
                          {clinics.map(clinic => (
                            <SelectItem key={clinic.id} value={clinic.id}>{clinic.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="col-span-2">
                    <Label>Address</Label>
                    <Input
                      value={newPatient.address}
                      onChange={(e) => setNewPatient({...newPatient, address: e.target.value})}
                      placeholder="Full address"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Medical History</Label>
                    <Input
                      value={newPatient.medical_history}
                      onChange={(e) => setNewPatient({...newPatient, medical_history: e.target.value})}
                      placeholder="Relevant medical conditions"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-sky-600 hover:bg-sky-700" data-testid="save-patient-btn">
                    Create Patient
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search patients by name or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="patient-search-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Patients List */}
        <div className="grid gap-4">
          {loading ? (
            [1, 2, 3].map(i => (
              <div key={i} className="skeleton h-24 rounded-xl"></div>
            ))
          ) : filteredPatients.length > 0 ? (
            filteredPatients.map(patient => (
              <Card 
                key={patient.id} 
                className="card-hover cursor-pointer"
                onClick={() => navigate(`/patients/${patient.id}`)}
                data-testid={`patient-card-${patient.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-sky-100 rounded-full flex items-center justify-center">
                        <Users className="w-6 h-6 text-sky-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{patient.name}</h3>
                        <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                          <span>{patient.age} yrs • {patient.gender}</span>
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {patient.phone}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/pta-test/${patient.id}`);
                        }}
                      >
                        <Ear className="w-4 h-4 mr-1" />
                        New Test
                      </Button>
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </div>
                  {patient.medical_history && (
                    <p className="text-sm text-slate-500 mt-3 pl-16">
                      Medical History: {patient.medical_history}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No patients found</p>
                <Button 
                  variant="link" 
                  className="mt-2"
                  onClick={() => setDialogOpen(true)}
                >
                  Add your first patient
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
